// What an agent costs, and what a tier implies before one has ever run.
//
// Prices are DATA, deliberately. They change — one model's introductory rate
// has already lapsed — and a table baked into code silently misreports from the
// day it drifts. Every stored cost records which version priced it, so history
// stays explainable after a change instead of being quietly rewritten.

export const PRICE_VERSION = '2026-09';

/**
 * Per million tokens, by the tier a user actually picks. The tier is the unit
 * here because that is what the agent stores and what the user chose; which
 * model sits behind a tier is a product decision that can move without every
 * historical figure becoming wrong.
 */
export const TIER_PRICING = {
  fast:     { label: 'Efficient', inputPerM: 1, outputPerM: 5 },
  standard: { label: 'Balanced',  inputPerM: 3, outputPerM: 15 },
  smart:    { label: 'Expert',    inputPerM: 5, outputPerM: 25 },
};

export const DEFAULT_TIER = 'standard';

// Cached input is billed at a fraction of fresh input; writing to the cache
// costs a small premium over it. This is the single biggest lever on agent
// cost, because the charter and knowledge an agent re-reads never change
// between runs.
const CACHE_READ_MULTIPLIER  = 0.1;
const CACHE_WRITE_MULTIPLIER = 1.25;

export function tierPricing(tier) {
  return TIER_PRICING[tier] || TIER_PRICING[DEFAULT_TIER];
}

/**
 * Cost in USD of one run's actual usage. Pass what the API reported — never a
 * reconstruction from an estimate.
 */
export function costOfUsage(usage = {}, tier = DEFAULT_TIER) {
  const p = tierPricing(tier);
  const input      = Math.max(0, usage.inputTokens      || 0);
  const output     = Math.max(0, usage.outputTokens     || 0);
  const cacheRead  = Math.max(0, usage.cacheReadTokens  || 0);
  const cacheWrite = Math.max(0, usage.cacheWriteTokens || 0);
  return (
    (input      / 1e6) * p.inputPerM +
    (output     / 1e6) * p.outputPerM +
    (cacheRead  / 1e6) * p.inputPerM * CACHE_READ_MULTIPLIER +
    (cacheWrite / 1e6) * p.inputPerM * CACHE_WRITE_MULTIPLIER
  );
}

/** Short money for a UI. Sub-cent amounts are real but not worth four decimals. */
export function formatUsd(usd) {
  if (!isFinite(usd) || usd <= 0) return '$0.00';
  if (usd < 0.01) return '<$0.01';
  if (usd < 1)    return `$${usd.toFixed(2)}`;
  if (usd < 1000) return `$${usd.toFixed(2)}`;
  return `$${(usd / 1000).toFixed(1)}k`;
}

/**
 * The three figures that actually tell a user something, per the spec.
 *
 * Total alone finds the busiest agent, not the wasteful one. Cost per completed
 * outcome is what exposes an agent burning runs that end in failure — cheap per
 * run, expensive per result.
 */
export function agentSpend(runs = []) {
  const total     = runs.reduce((s, r) => s + (r.cost?.usd || 0), 0);
  const completed = runs.filter(r => r.outcome === 'completed' || r.status === 'completed').length;
  return {
    runs: runs.length,
    completed,
    totalUsd: total,
    perRunUsd: runs.length ? total / runs.length : 0,
    // Undefined rather than Infinity when nothing has succeeded: "no completed
    // runs yet" is a different statement from "infinitely expensive", and a UI
    // should say so rather than render ∞.
    perCompletedUsd: completed ? total / completed : undefined,
  };
}

/**
 * Rough cost of a typical task at a tier, for choosing one BEFORE any agent has
 * run. Deliberately coarse and labelled as an estimate wherever it is shown —
 * real spend varies more with the size of the job than with the tier.
 */
export function estimateTypicalTask(tier = DEFAULT_TIER, task = {}) {
  const inputTokens      = task.inputTokens      ?? 500_000;
  const outputTokens     = task.outputTokens     ?? 40_000;
  const cachedFraction   = task.cachedFraction   ?? 0.7;
  const fresh  = Math.round(inputTokens * (1 - cachedFraction));
  const cached = inputTokens - fresh;
  return costOfUsage(
    { inputTokens: fresh, outputTokens, cacheReadTokens: cached },
    tier,
  );
}

/**
 * How an agent's spend compares with the others, as a band a dot can show.
 *
 * Relative rather than absolute, because an absolute threshold means nothing
 * until a workspace knows its own usage — £50 a month is alarming for one team
 * and trivial for another. What a user can actually act on is "this agent is
 * eating most of the budget".
 *
 * The floor stops a workspace that has spent pennies from seeing a red dot: a
 * warning that fires when nothing is wrong is quickly learned to be ignored,
 * and then it is not there when something is.
 */
export function spendBand(usd, allUsd = [], opts = {}) {
  const floorUsd = opts.floorUsd ?? 1;
  if (!(usd > 0)) return 'none';
  if (usd < floorUsd) return 'low';
  const total = allUsd.reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
  if (total <= 0) return 'low';
  const share = usd / total;
  if (share >= 0.5)  return 'high';
  if (share >= 0.25) return 'moderate';
  return 'low';
}

export const SPEND_BAND_META = {
  none:     { color: '#94a3b8', label: 'Nothing spent yet' },
  low:      { color: '#10b981', label: 'Low share of spend' },
  moderate: { color: '#f59e0b', label: 'A noticeable share of spend' },
  high:     { color: '#ef4444', label: 'Most of the spend' },
};

/**
 * What a whole team costs: the sum of its agents.
 *
 * Kept as a sum rather than a separate stored figure so a team total can never
 * disagree with the agents it is made of — the commonest way a cost display
 * loses a user's trust.
 */
export function functionSpend(agents = [], perAgentUsd = (a) => estimateTypicalTask(a.model)) {
  const figures = agents.map(perAgentUsd);
  return {
    agents: agents.length,
    totalUsd: figures.reduce((s, v) => s + (v > 0 ? v : 0), 0),
    figures,
  };
}

/**
 * What one agent is doing and what it has cost.
 *
 * Three different figures because they answer three different questions, and a
 * single total answers none of them well: is this agent expensive *right now*,
 * has it been expensive *today*, and has it been expensive *ever*.
 *
 * `nowMs` is passed in so this never depends on the clock and can be tested.
 */
/**
 * A run still marked running long after it began is almost certainly not
 * running — something failed to collect it. An agent stuck on "Working"
 * forever is indistinguishable from a slow one, which is exactly how a broken
 * collector went unnoticed for an afternoon.
 */
const STALLED_AFTER_MS = 20 * 60 * 1000;

export function isStalled(run, nowMs = Date.now()) {
  if (!run || run.status !== 'running') return false;
  const started = Date.parse(run.startedAt || '');
  if (isNaN(started)) return false;
  return (nowMs - started) > STALLED_AFTER_MS;
}

export function agentStats(runs = [], agentId, nowMs = Date.now(), chats = []) {
  const mine = runs.filter(r => r.agentId === agentId);
  // Talking to an agent costs money too, and it was being left out entirely —
  // a chat window showing $0.02 beside an overview showing $0.00 makes both
  // figures untrustworthy, and the user cannot tell which one is lying.
  const myChats = chats.filter(c => c.agentId === agentId);
  const chatUsd = myChats.reduce((s, c) => s + (c.usdCost > 0 ? c.usdCost : 0), 0);
  const dayAgo = nowMs - 24 * 60 * 60 * 1000;

  const active = mine.filter(r => r.status === 'running');
  const sum = (list) => list.reduce((s, r) => s + (r.usdCost > 0 ? r.usdCost : 0), 0);

  const startedMs = (r) => {
    const t = Date.parse(r.startedAt || '');
    return isNaN(t) ? 0 : t;
  };

  const stalled = active.filter(r => isStalled(r, nowMs));

  return {
    // "Working" only if a run is genuinely open. An agent with no runs is idle,
    // not broken, and should not be shown as an error state — but one that has
    // been "working" for twenty minutes is a stall, and saying so is the
    // difference between a visible problem and a silent one.
    status: stalled.length ? 'stalled' : active.length ? 'working' : 'idle',
    stalledRuns: stalled.length,
    activeRuns: active.length,
    // Spend on the task in hand — the figure that lets someone stop a runaway
    // before it finishes rather than discover it afterwards.
    currentUsd: sum(active),
    currentTask: active[0]?.task || null,
    currentCapUsd: active[0]?.capUsd ?? null,
    last24Usd: sum(mine.filter(r => startedMs(r) >= dayAgo)),
    // Chat spend is cumulative per conversation rather than per message, so it
    // counts towards the lifetime total and is shown separately rather than
    // being split across a 24-hour window it cannot be attributed to.
    lifetimeUsd: sum(mine) + chatUsd,
    chatUsd,
    chats: myChats.length,
    runs: mine.length,
    completed: mine.filter(r => r.status === 'completed').length,
    capped: mine.filter(r => r.status === 'capped').length,
    failed: mine.filter(r => r.status === 'failed').length,
  };
}

/** The same three figures for a whole team, summed from its agents. */
export function functionStats(runs = [], agents = [], nowMs = Date.now(), chats = []) {
  const per = agents.map(a => agentStats(runs, a.id, nowMs, chats));
  return {
    agents: agents.length,
    working: per.filter(s => s.status === 'working').length,
    currentUsd:  per.reduce((s, x) => s + x.currentUsd, 0),
    last24Usd:   per.reduce((s, x) => s + x.last24Usd, 0),
    lifetimeUsd: per.reduce((s, x) => s + x.lifetimeUsd, 0),
    runs:        per.reduce((s, x) => s + x.runs, 0),
    per,
  };
}

/**
 * How close a running task is to its cap, 0..1. Undefined when there is no cap
 * or nothing running — a bar at zero would imply a limit that is not there.
 */
export function capProgress(stats) {
  if (stats.status !== 'working' || !(stats.currentCapUsd > 0)) return undefined;
  return Math.min(1, stats.currentUsd / stats.currentCapUsd);
}
